import type { DateRange } from '../../shared/travel_calendar.js';
import { parseDateOnly, toDateOnly } from '@voll-intelligence/dates';

/*
 * A JANELA da locação em data-hora LOCAL-NAIVE (`YYYY-MM-DDTHH:mm`, a hora do
 * balcão, sem fuso) montada a partir do que o painel do período pergunta: os
 * dias num calendário só (retirada → devolução) e as duas HORAS nas réguas em
 * cima dele (#4513, o desenho do Figma). Puro: é aqui que moram as regras de
 * piso, e elas precisam ser testáveis sem montar a barra.
 */

/** A hora que a barra propõe antes de a pessoa mexer — o horário comercial da
 *  retirada, que a locadora costuma sugerir. */
export const DEFAULT_TIME = '10:00';
/** O passo das réguas de hora: meia hora, a grade do balcão. */
export const SLOT_MINUTES = 30;
const LAST_SLOT = '23:30';
/** A última meia-hora do dia em minutos — a ponta direita da régua. */
export const LAST_SLOT_MINUTES = 23 * 60 + 30;

export type RentalTimes = { readonly pickup: string; readonly dropoff: string };
export type RentalWindow = { readonly pickupAt?: string; readonly dropoffAt?: string };

/** `YYYY-MM-DDTHH:mm` → a data. */
export function dateOf(value: string | undefined): string | undefined {
  return value ? value.slice(0, 10) : undefined;
}

/** `YYYY-MM-DDTHH:mm` → a hora; sem hora na string, `undefined`. */
export function timeOf(value: string | undefined): string | undefined {
  return value ? value.slice(11, 16) || undefined : undefined;
}

function joinLocalDateTime(date: string, time: string): string {
  return `${date}T${time}`;
}

/** O próximo múltiplo de meia hora DEPOIS do instante — o piso da devolução a
 *  partir da retirada, e a base do piso da retirada (`pickupFloorFrom`). Sempre
 *  estritamente depois: uma retirada às 18:00 abre a devolução em 18:30; agora
 *  09:10 abre a retirada em 09:30. Passa pelo `Date` local para a virada de
 *  dia/mês sair certa. */
export function nextSlotAfter(value: string): string {
  const date = localDateTime(value);
  date.setMinutes(Math.floor(date.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES + SLOT_MINUTES);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${toDateOnly(date)}T${hh}:${mm}`;
}

/** `YYYY-MM-DDTHH:mm` → `Date` LOCAL (a string não tem fuso; o parse dela
 *  como UTC deslocaria a hora do balcão). */
function localDateTime(value: string): Date {
  return new Date(
    Number(value.slice(0, 4)),
    Number(value.slice(5, 7)) - 1,
    Number(value.slice(8, 10)),
    Number(value.slice(11, 13) || 0),
    Number(value.slice(14, 16) || 0),
  );
}

/** A antecedência mínima da retirada: o rental-car-api recusa a busca com
 *  `OUTSIDE_ANTECEDENCE` abaixo de 2 h, então a barra nem oferece (#4513). */
const PICKUP_LEAD_MINUTES = 120;

/** O primeiro instante escolhível para a retirada: a meia-hora que chega a
 *  `PICKUP_LEAD_MINUTES` de `now` (inclusiva — agora 13:00 abre 15:00). */
export function pickupFloorFrom(now: string): string {
  const lead = localDateTime(now);
  lead.setMinutes(lead.getMinutes() + PICKUP_LEAD_MINUTES - 1);
  const hh = String(lead.getHours()).padStart(2, '0');
  const mm = String(lead.getMinutes()).padStart(2, '0');
  return nextSlotAfter(`${toDateOnly(lead)}T${hh}:${mm}`);
}

/** As diárias da janela: períodos de 24 h começados, a régua da locadora e do
 *  BFF (`search_offers`) — 20/10 13:30 → 23/10 14:00 são 4, não 3 (#4513). */
export function rentalDayCount(pickupAt: string, dropoffAt: string): number {
  const minutes = (localDateTime(dropoffAt).getTime() - localDateTime(pickupAt).getTime()) / 60_000;
  return Math.max(1, Math.ceil(minutes / (24 * 60)));
}

/** Agora, no minuto local. Um minuto já começado (segundos > 0) arredonda para
 *  cima: às 13:00:59 o piso não pode abrir 15:00, a menos de 2 h (#4513). */
export function nowLocal(): string {
  const now = new Date();
  if (now.getSeconds() > 0 || now.getMilliseconds() > 0) {
    now.setSeconds(60, 0);
  }
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${toDateOnly(now)}T${hh}:${mm}`;
}

/** O piso desmontado: o primeiro dia escolhível e, NESSE dia, a primeira hora.
 *  Um piso já depois da última meia-hora do dia empurra o piso para o dia
 *  seguinte inteiro — senão o dia ficaria escolhível sem hora nenhuma. */
export function splitFloor(floor: string): { day: string; time: string } {
  const day = floor.slice(0, 10);
  const time = floor.slice(11, 16) || '00:00';
  if (time <= LAST_SLOT) return { day, time };
  const next = parseDateOnly(day);
  if (!next) return { day, time };
  next.setDate(next.getDate() + 1);
  return { day: toDateOnly(next), time: '00:00' };
}

/** `HH:mm` → minutos do dia (a posição na régua). */
export function minutesOfTime(time: string): number {
  return Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));
}

/** Minutos do dia → `HH:mm`. */
export function timeOfMinutes(minutes: number): string {
  const hh = String(Math.floor(minutes / 60)).padStart(2, '0');
  const mm = String(minutes % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

/** `HH:mm` rotulado no idioma do host ("13:30", "1:30 PM"). */
export function formatSlotTime(locale: string, time: string): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(2000, 0, 1, Number(time.slice(0, 2)), Number(time.slice(3, 5))),
  );
}

/** A hora da retirada que vale no dia escolhido: no dia do piso, nunca antes
 *  da hora do piso (hoje, uma hora que já passou ou a menos de 2 h). A régua
 *  mostra esta hora, então o ajuste fica à vista — não é troca em silêncio. */
export function effectivePickupTime(
  day: string | undefined,
  time: string,
  pickupFloor: string,
): string {
  const floor = splitFloor(pickupFloor);
  return day === floor.day && time < floor.time ? floor.time : time;
}

/**
 * A janela a partir do período e das horas escolhidas: a retirada com o piso
 * aplicado (`effectivePickupTime`) e a devolução EXATAMENTE como escolhida.
 * A devolução nunca é empurrada (#4513: retirada 23:30 com devolução 10:00 no
 * mesmo dia virava 00:00 do dia seguinte, uma locação de meia hora que ninguém
 * pediu) — uma janela invertida volta invertida, e quem decide é
 * `isInvertedWindow`. Sem retirada não há janela; sem devolução, só a retirada.
 */
export function composeRentalWindow(
  range: DateRange,
  times: RentalTimes,
  pickupFloor: string,
): RentalWindow {
  if (!range.from) return {};
  const pickupAt = joinLocalDateTime(
    range.from,
    effectivePickupTime(range.from, times.pickup, pickupFloor),
  );
  if (!range.to) return { pickupAt };
  return { pickupAt, dropoffAt: joinLocalDateTime(range.to, times.dropoff) };
}

/** A retirada mais tarde num período de um dia só: a última meia-hora fica
 *  para a devolução. */
const SAME_DAY_LAST_PICKUP_MINUTES = LAST_SLOT_MINUTES - SLOT_MINUTES;

/** O que as réguas deixam escolher no período escolhido. No MESMO dia (#5662)
 *  a retirada para em 23:00 e a devolução começa meia hora depois da retirada
 *  que vale (`effectivePickupTime`). Em dias diferentes, o dia inteiro. */
export function timeBounds(
  range: DateRange,
  pickup: string,
  pickupFloor: string,
): { readonly pickupMax: string | undefined; readonly dropoffMin: string | undefined } {
  if (!range.from || range.from !== range.to)
    return { pickupMax: undefined, dropoffMin: undefined };
  const effective = minutesOfTime(effectivePickupTime(range.from, pickup, pickupFloor));
  return {
    pickupMax: timeOfMinutes(SAME_DAY_LAST_PICKUP_MINUTES),
    dropoffMin: timeOfMinutes(Math.min(effective + SLOT_MINUTES, LAST_SLOT_MINUTES)),
  };
}

/**
 * As horas em ordem para o período (#5662): no mesmo dia a inversão só pode
 * ser de hora, e quem a resolve são as réguas — a retirada não passa de 23:00,
 * e subir a retirada ARRASTA a devolução para meia hora depois dela. Sem
 * estado de erro. Em dias diferentes, as horas saem como vieram. Um piso de
 * hoje depois das 23:00 não deixa período de um dia: a janela sai invertida e
 * o Confirmar trava (`isInvertedWindow`).
 */
export function orderTimes(range: DateRange, times: RentalTimes, pickupFloor: string): RentalTimes {
  if (!range.from || range.from !== range.to) return times;
  const pickup = timeOfMinutes(Math.min(minutesOfTime(times.pickup), SAME_DAY_LAST_PICKUP_MINUTES));
  const { dropoffMin } = timeBounds(range, pickup, pickupFloor);
  const dropoff =
    dropoffMin !== undefined && times.dropoff < dropoffMin ? dropoffMin : times.dropoff;
  return { pickup, dropoff };
}

/** A devolução não vem DEPOIS da retirada — não é locação. O painel não deixa
 *  isso acontecer (`orderTimes`); fica como cinto para valores que não passaram
 *  por ele (URL, pedido antigo): a barra trava o Buscar. */
export function isInvertedWindow(window: RentalWindow): boolean {
  return (
    window.pickupAt !== undefined &&
    window.dropoffAt !== undefined &&
    window.dropoffAt <= window.pickupAt
  );
}
